import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthApiService } from './auth-api.service';
import { AuthStore } from './auth-store.service';
import { AppHttpService } from '../../../services/app-http.service';
import { API_MAP } from '../../../core/api-map/api-map';
import {CarderUser} from '../models';

@Injectable({
  providedIn: 'root'
})
export class EmailVerificationService {
  private isVerifiedSubject = new BehaviorSubject<boolean>(false);
  private lastSentTimeSubject = new BehaviorSubject<number>(0);
  private cooldownSecondsSubject = new BehaviorSubject<number>(0);

  isVerified$ = this.isVerifiedSubject.asObservable();
  lastSentTime$ = this.lastSentTimeSubject.asObservable();
  cooldownSeconds$ = this.cooldownSecondsSubject.asObservable();

  private cooldownInterval: any;
  private readonly COOLDOWN_DURATION = 60; // 60 seconds

  constructor(
    private authApiService: AuthApiService,
    private authStore: AuthStore,
    private httpService: AppHttpService
  ) {
    // Initialize verification status from auth store
    this.authStore.user$.subscribe(user => {
      if (user) {
        this.isVerifiedSubject.next(user.isVerified || false);
      }
    });
  }

  /**
   * Manually check verification status from server
   */
  checkVerification(): Observable<boolean> {
    return this.httpService.request<CarderUser>({
      ...API_MAP.getUserMe
    }).pipe(
      tap(response => {
        if (response) {
          const isVerified = response.isVerified || false;
          this.isVerifiedSubject.next(isVerified);

          // Update auth store with new verification status
          this.authStore.updateUser({
            isVerified,
            emailVerifiedAt: response.emailVerifiedAt
          });
        }
      }),
      map(response => response?.isVerified || false),
      catchError(() => of(false))
    );
  }

  /**
   * Resend verification email
   */
  resendVerificationEmail(): Observable<{ message: string }> {
    // Check if still in cooldown
    const now = Date.now();
    const lastSent = this.lastSentTimeSubject.value;
    const timeSinceLastSent = (now - lastSent) / 1000;

    if (timeSinceLastSent < this.COOLDOWN_DURATION) {
      throw new Error(`Please wait ${Math.ceil(this.COOLDOWN_DURATION - timeSinceLastSent)} seconds before sending again`);
    }

    return this.authApiService.resendEmailVerification().pipe(
      tap(() => {
        // Start cooldown
        this.startCooldown();
      })
    );
  }

  /**
   * Start cooldown timer
   */
  private startCooldown(): void {
    this.lastSentTimeSubject.next(Date.now());
    this.cooldownSecondsSubject.next(this.COOLDOWN_DURATION);

    // Clear existing interval if any
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }

    // Start countdown
    this.cooldownInterval = setInterval(() => {
      const current = this.cooldownSecondsSubject.value;
      if (current > 0) {
        this.cooldownSecondsSubject.next(current - 1);
      } else {
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
      }
    }, 1000);
  }

  /**
   * Get remaining cooldown seconds
   */
  getRemainingCooldown(): number {
    const now = Date.now();
    const lastSent = this.lastSentTimeSubject.value;
    const timeSinceLastSent = (now - lastSent) / 1000;

    if (timeSinceLastSent >= this.COOLDOWN_DURATION) {
      return 0;
    }

    return Math.ceil(this.COOLDOWN_DURATION - timeSinceLastSent);
  }

  /**
   * Check if can send email (not in cooldown)
   */
  canSendEmail(): boolean {
    return this.getRemainingCooldown() === 0;
  }

  /**
   * Clean up resources
   */
  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }
}
